import React, { useEffect, useState } from "react";
import {
  Modal,
  Tabs,
  Input,
  Button,
  Alert,
  Spin,
  message,
  Upload,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import SpecEditor from "./SpecEditor";
import SpecTabsView from "./SpecTabsView";
import {
  buildSpecWorkbook,
  validateSpecModel,
  toEditableModel,
} from "../../helper/specWorkbook";
import { parseSpecWorkbook } from "../../helper/spec_parser";
import { fetchSpecBytes, editFSD } from "../../service/fsd.service";
import { postFormData } from "../../helper/axios_helper";

// Edit a spec two ways — inline field editor or re-upload — both Save through the
// same versioned backend pipeline (POST /filespec/edit with an .xlsx).
function EditSpecModal({ open, record, onClose, onSaved }) {
  const specName = record?.specName;

  const [tab, setTab] = useState("fields");
  const [loadingSeed, setLoadingSeed] = useState(false);
  const [model, setModel] = useState(null);
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  // Re-upload tab state
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [validating, setValidating] = useState(false);
  const [uploadValid, setUploadValid] = useState(false);

  useEffect(() => {
    if (!open || !specName) return;
    setTab("fields");
    setNote("");
    setErrors([]);
    setFile(null);
    setParsed(null);
    setUploadValid(false);
    setLoadingSeed(true);
    fetchSpecBytes(specName)
      .then((buf) => setModel(toEditableModel(parseSpecWorkbook(buf), specName)))
      .catch(() =>
        setModel(toEditableModel({ fileLevel: [], columnLevel: { rows: [] }, allowableValues: { rows: [] } }, specName)),
      )
      .finally(() => setLoadingSeed(false));
  }, [open, specName]);

  const documentFileType = record?.documentFileType || "xlsx";

  const requestBody = {
    specName,
    comments: record?.comments || "",
    category: record?.category || "",
    documentFileType,
    note,
    regenerateSuite: true,
  };

  const saveFields = async () => {
    const errs = validateSpecModel(model);
    setErrors(errs);
    if (errs.length) {
      message.error("Please fix the highlighted issues before saving.");
      return;
    }
    setSaving(true);
    try {
      const generated = buildSpecWorkbook(model);
      await editFSD(generated, requestBody);
      message.success("Spec updated — a new version was saved.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to save spec.");
    } finally {
      setSaving(false);
    }
  };

  const onPickFile = async (f) => {
    setFile(f);
    setUploadValid(false);
    setParsed(null);
    setValidating(true);
    try {
      const fd = new FormData();
      fd.append("fileUpload", f);
      const res = await postFormData("/api/auth/filespec/validate", fd);
      const ok = res?.data?.message === "Pass" || res?.data?.isSuccessful;
      if (!ok) {
        message.error("The file failed validation. Check the template structure.");
        return false;
      }
      const buf = await f.arrayBuffer();
      setParsed(parseSpecWorkbook(buf));
      setUploadValid(true);
    } catch {
      message.error("Could not validate the file.");
    } finally {
      setValidating(false);
    }
    return false; // prevent AntD auto-upload
  };

  const saveUpload = async () => {
    if (!file || !uploadValid) return;
    setSaving(true);
    try {
      await editFSD(file, requestBody);
      message.success("Spec updated from file — a new version was saved.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to save spec.");
    } finally {
      setSaving(false);
    }
  };

  const footer =
    tab === "fields"
      ? [
          <Button key="cancel" onClick={onClose}>Cancel</Button>,
          <Button key="save" type="primary" loading={saving} onClick={saveFields}>
            Save new version
          </Button>,
        ]
      : [
          <Button key="cancel" onClick={onClose}>Cancel</Button>,
          <Button
            key="save"
            type="primary"
            loading={saving}
            disabled={!uploadValid}
            onClick={saveUpload}
          >
            Save new version
          </Button>,
        ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="80vw"
      style={{ maxWidth: 1100 }}
      styles={{ body: { height: "70vh", overflowY: "auto", overflowX: "hidden" } }}
      centered
      destroyOnClose
      maskClosable={false}
      title={`Edit spec — ${specName || ""}`}
      footer={footer}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Saving creates a new version and regenerates this spec's default test suite. Any custom suites built on this spec may need review."
      />
      <Input.TextArea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Change note (optional)"
        autoSize={{ minRows: 1, maxRows: 2 }}
        maxLength={500}
        style={{ marginBottom: 12 }}
      />
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: "fields",
            label: "Edit fields",
            children: loadingSeed ? (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Spin />
              </div>
            ) : (
              <>
                {errors.length > 0 && (
                  <Alert
                    type="error"
                    style={{ marginBottom: 10 }}
                    message={`${errors.length} issue${errors.length === 1 ? "" : "s"} to fix`}
                    description={
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {errors.slice(0, 8).map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    }
                  />
                )}
                <SpecEditor value={model} onChange={setModel} />
              </>
            ),
          },
          {
            key: "upload",
            label: "Re-upload file",
            children: (
              <div>
                <Upload.Dragger
                  multiple={false}
                  accept=".xlsx"
                  beforeUpload={onPickFile}
                  showUploadList={false}
                  disabled={validating}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {file ? file.name : "Click or drag a corrected .xlsx here"}
                  </p>
                </Upload.Dragger>
                {validating && (
                  <div style={{ textAlign: "center", padding: 16 }}>
                    <Spin /> Validating…
                  </div>
                )}
                {uploadValid && parsed && (
                  <div style={{ marginTop: 12 }}>
                    <Alert
                      type="success"
                      showIcon
                      message="Validation passed — review below, then save."
                      style={{ marginBottom: 10 }}
                    />
                    <SpecTabsView
                      fileLevel={parsed.fileLevel}
                      columnLevel={parsed.columnLevel}
                      allowableValues={parsed.allowableValues}
                    />
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />
    </Modal>
  );
}

export default EditSpecModal;
